import type { IOgqlV1Column } from '@opengov/ogql-types';
import { accountSummaryRounders, columnSemanticTypeToFormatterType } from '@opengov/reporting-formatters';
import {
  ColumnDefinition,
  ColumnValue,
  ColumnValueSegment,
  CustomColDef,
  EGroupType,
  IAccountSummary,
  IColumnSource,
  isCalculationExpression,
  isMultiHierarchyCalculatedRowGroup,
  isMultiHierarchyRowGroupLike,
  MultiHierarchyAccountSummary,
  MultiHierarchyCalculatedRowGroup,
  MultiHierarchyRowGroup,
  MultiHierarchyRowOverride
} from '@opengov/reporting-types';
import { head } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isNil from 'lodash/isNil';

export const ROW_GROUPING_ALIAS_PREFIX = 'row_grouping';
export const COLUMN_GROUPING_0_ALIAS = 'column_grouping_0';

export class MultiGroupingOverflowError extends Error {
  constructor() {
    super();
    this.message = 'Unable to expand node. Group size is less than the node level.';
  }
}

export interface ParentRowsAgGrid {
  id: string | undefined;
  rowGroupIndex: number;
  row_group_0: string;
  values: any[];
  /** The row group's parent response has not arrived yet; value cells render as loading blocks. */
  pendingValues?: boolean;
  canExpand: boolean;
  groupData: {
    type:
      | EGroupType.Expression
      | EGroupType.COA
      | EGroupType.Override
      | EGroupType.MultiHierarchy
      | EGroupType.MultiHierarchyOverride
      | EGroupType.MultiHierarchyCalculated
      | undefined;
    metadataColumns: IOgqlV1Column[];
  };
}

export function flattenColumnDefinitions(columnGroups: IColumnSource[], columnDefinitions: ColumnDefinition[]) {
  const colGroups = cloneDeep(columnGroups);

  return columnDefinitions
    .filter((col) => 'unique' in col)
    .flatMap((col, idx) => {
      const columnGroup = isColumnGroupingField(col.field as string)
        ? colGroups[idx]
        : findColumnGroupForColumn(colGroups, col.field as string);
      if (isCalculationExpression(columnGroup as IColumnSource)) {
        (col.children || [col]).forEach((child) => {
          (child as CustomColDef).type = columnGroup?.type as EGroupType;
        });
      }
      return (col.children || [col]) as CustomColDef[];
    });
}

function isColumnGroupingField(field: string) {
  return field === COLUMN_GROUPING_0_ALIAS;
}

function parseColumnGroupIdFromField(field: string) {
  return head(field.split('.'));
}

function findColumnGroupForColumn(colGroups: IColumnSource[], field: string) {
  return colGroups.find((group) => group.id === parseColumnGroupIdFromField(field));
}

export function roundAndCheckValueIsNotZero(
  val: ColumnValueSegment,
  index: number,
  metadataColumns: IOgqlV1Column[],
  flatColumnDefs: CustomColDef[],
  formatting: IAccountSummary['formatting'],
  isParentRow: boolean = false
): boolean {
  if (isNil(val)) return false;
  const metadataColumn = metadataColumns[index];

  if (metadataColumn && !metadataColumn.name.startsWith(ROW_GROUPING_ALIAS_PREFIX)) {
    // Need to subtract 1 from the index since the first value in the row is the row group name
    const columnDefIndex = isParentRow ? index : index - 1;

    if (columnDefIndex >= 0 && columnDefIndex < flatColumnDefs.length) {
      const columnDef = flatColumnDefs[columnDefIndex] as IColumnSource;
      const type = isCalculationExpression(columnDef)
        ? columnDef.display
        : columnSemanticTypeToFormatterType(metadataColumn);

      const roundedValue = type ? accountSummaryRounders(formatting)[type](val).roundedValue : val;

      return roundedValue !== 0 && !isNil(roundedValue);
    }
  }
  return false;
}

export function filterZerosInChildRows(
  childRows: ColumnValue[],
  metadataColumns: IOgqlV1Column[],
  formatting: IAccountSummary['formatting'],
  columnGroups: IColumnSource[],
  columnDefinitions: ColumnDefinition[]
) {
  const flatColumnDefs = flattenColumnDefinitions(columnGroups, columnDefinitions);

  return childRows.reduce<ColumnValue[]>((filteredRows, childRow) => {
    let shouldIncludeRow = false;

    for (let index = 0; index < childRow.length; index++) {
      if (roundAndCheckValueIsNotZero(childRow[index], index, metadataColumns, flatColumnDefs, formatting)) {
        shouldIncludeRow = true;
        break;
      }
    }

    if (shouldIncludeRow) filteredRows.push(childRow);

    return filteredRows;
  }, []);
}

export function filterZerosInParentRows(
  parentRows: ParentRowsAgGrid[],
  metadataColumnsPerRow: IOgqlV1Column[][],
  formatting: IAccountSummary['formatting'],
  columnGroups: IColumnSource[],
  columnDefinitions: ColumnDefinition[]
) {
  const flatColumnDefs = flattenColumnDefinitions(columnGroups, columnDefinitions);

  return parentRows.reduce<ParentRowsAgGrid[]>((filteredRows, row, rowIndex) => {
    // A pending row has no values yet, so suppression has nothing to judge; dropping it would hide
    // the loading state of a row group that may well have data.
    if (row.pendingValues) {
      filteredRows.push(row);
      return filteredRows;
    }

    let shouldIncludeRow = false;

    for (let idx = 0; idx < row.values.length; idx++) {
      const val = row.values[idx];
      if (
        roundAndCheckValueIsNotZero(
          val,
          idx,
          metadataColumnsPerRow[rowIndex].slice(1),
          flatColumnDefs,
          formatting,
          true
        )
      ) {
        shouldIncludeRow = true;
        break;
      }
    }

    if (shouldIncludeRow) {
      filteredRows.push(row);
    }

    return filteredRows;
  }, []);
}

export function shouldSuppressZerosInRows(report: IAccountSummary | MultiHierarchyAccountSummary) {
  if (!report || !report.formatting || !report.formatting.zeroNumbers) return false;
  return report.formatting.zeroNumbers[0].enabled;
}

export function collect<P>(items: unknown[], path: string | string[], map = (s: unknown) => s): P[] {
  return items.reduce<P[]>((results, next) => results.concat(map(get(next, path) ?? []) as P[]), []);
}

/**
 * `items.map(fn)` with at most `limit` calls in flight at once; results keep item order. A missing
 * or non-positive limit behaves like `Promise.all` — and `product-nextgen-reporting-max-parallel-requests`
 * defaults to 0, so at today's values every fan-out below still runs unbounded; the cap only
 * exists so the flag can be tuned down without a release. Rejects with the first error, like
 * `Promise.all`; the remaining workers still drain the list in the background.
 *
 * Caveats for whoever tunes the flag: the cap is PER FAN-OUT — the prefetch keeps its own pool, so
 * distinct URLs can run at about twice the flag — and a value below a report's row-group count
 * delays later level-0 rows: the uncached grid path paints only after every parent returns, and
 * the cached path leaves later groups' values blank (no loading cell) until a worker frees up, a
 * slowdown the load gauge does not record. `0` still means "prefetch off, grid unbounded".
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number | undefined,
  fn: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const workerCount = limit && limit > 0 ? Math.min(limit, items.length) : items.length;
  if (workerCount >= items.length) {
    return Promise.all(items.map(fn));
  }

  const results = new Array<R>(items.length);
  let cursor = 0;
  const runNext = async (): Promise<void> => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await fn(items[index], index);
    }
  };
  await Promise.all(Array.from({ length: workerCount }, runNext));
  return results;
}

/**
 * A multi-hierarchy row source that produces real (non-calculated) grid rows: either a plain row
 * group or an ERP override row.
 *
 * The two are interchangeable everywhere the grid is concerned — an override row only redirects
 * where its per-column measures come from, and still carries the same
 * `id`/`displayName`/`groupDisplayNames`/`grouping` used to fetch, label, render and drill it.
 */
export type MultiHierarchyDataRowSource = MultiHierarchyRowGroup | MultiHierarchyRowOverride;

/**
 * Every grid decision about "is this a drillable multi-hierarchy row" must go through this rather
 * than `isMultiHierarchyRowGroup` alone, which silently drops ERP override rows.
 *
 * `isMultiHierarchyRowGroupLike` in reporting-types is the same predicate but is module-private.
 */
export const isMultiHierarchyDataRowSource = isMultiHierarchyRowGroupLike;

export function getDeletedGroupLevel({
  deletedIds,
  rowGroupId,
  rowGroup
}: {
  deletedIds: number[] | undefined;
  rowGroupId: number;
  rowGroup: MultiHierarchyRowGroup | MultiHierarchyRowOverride | MultiHierarchyCalculatedRowGroup;
}): 'parent' | 'breakdown' | undefined {
  if (!deletedIds) {
    return undefined;
  }
  if (deletedIds.includes(rowGroupId)) {
    return 'parent';
  }
  // Calculated rows have no grouping levels at all. Both other flavours do — an ERP override row
  // inherits `grouping`, so its breakdown levels can reference a deleted rollup group just like a
  // plain multi-hierarchy row group's can.
  if (isMultiHierarchyCalculatedRowGroup(rowGroup)) {
    return undefined;
  }
  // grouping[0] is the parent level; grouping[1+] are breakdown levels.
  const breakdownLevels = rowGroup.grouping.slice(1);
  const breakdownGroupIds = breakdownLevels.flatMap((level) => level.groupIds.map(Number));
  if (breakdownGroupIds.some((id) => deletedIds.includes(id))) {
    return 'breakdown';
  }
  return undefined;
}
